import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Loader2, GripVertical, Sparkles } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import RepairabilityPanel from '../../components/admin/RepairabilityPanel';
import AIDescriptionPanel from '../../components/admin/AIDescriptionPanel';
import { createProduct, updateProduct, getProduct, uploadProductImage, deleteProductImage, getImageUrl, suggestCleanName, CATEGORY_LABELS } from '../../config';

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: '', category: 'pc', condition: 'reconditionne', grade: '',
    price: '', description: '', processor: '', ram: '', storage: '',
    screen_size: '', warranty: '6 mois', stock: 1, status: 'available',
    archived: false,
  });
  const [repairability, setRepairability] = useState({
    documentation: 5, demontabilite: 5, pieces_dispo: 5, prix_pieces: 5, logiciel: 5,
  });
  const [repairabilityStatus, setRepairabilityStatus] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      (async () => {
        try {
          const p = await getProduct(id);
          setForm({
            name: p.name || '', category: p.category || 'pc',
            condition: p.condition || 'reconditionne', grade: p.grade || '',
            price: p.price?.toString() || '', description: p.description || '',
            processor: p.processor || '', ram: p.ram || '', storage: p.storage || '',
            screen_size: p.screen_size || '', warranty: p.warranty || '6 mois',
            stock: p.stock || 1, status: p.status || 'available',
            archived: Boolean(p.archived),
          });
          setImages(p.images || []);
          if (p.repairability_scores) setRepairability(p.repairability_scores);
          if (p.repairability_status) setRepairabilityStatus(p.repairability_status);
        } catch { /* ignore */ }
        setLoading(false);
      })();
    }
  }, [id, isEdit]);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !isEdit) return;
    setUploading(true);
    for (const file of files) {
      if (images.length >= 5) break;
      try {
        const res = await uploadProductImage(id, file);
        setImages(prev => [...prev, res.filename]);
      } catch { /* ignore */ }
    }
    setUploading(false);
  };

  const handleImageDelete = async (filename) => {
    if (!isEdit) return;
    try {
      await deleteProductImage(id, filename);
      setImages(prev => prev.filter(f => f !== filename));
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 1,
        grade: form.grade || null,
        processor: form.processor || null,
        ram: form.ram || null,
        storage: form.storage || null,
        screen_size: form.screen_size || null,
        repairability_scores: repairability,
        repairability_status: repairabilityStatus,
      };

      if (isEdit) {
        await updateProduct(id, payload);
        navigate('/my/produits');
      } else {
        const newProduct = await createProduct(payload);
        navigate(`/my/produits/${newProduct.id}/modifier`);
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (loading) return (
    <AdminLayout title={isEdit ? 'Modifier le produit' : 'Nouveau produit'}>
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-secondary animate-spin" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title={isEdit ? 'Modifier le produit' : 'Nouveau produit'}>
      <div data-testid="admin-product-form">
        <button
          onClick={() => navigate('/my/produits')}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-secondary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux produits
        </button>

        {/* Layout : édition pleine largeur jusqu'à lg, puis split 2-col à
            partir de xl (édition gauche + aperçu live droit). Mobile/tablette
            gardent un seul col pour rester lisibles. */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-6 max-w-screen-2xl">
        <form onSubmit={handleSubmit} className="space-y-6 min-w-0">
          {/* Main info */}
          <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
            <h3 className="font-heading font-semibold text-primary">Informations générales</h3>

            <div>
              <label className="block text-xs text-slate-600 mb-1.5">Nom du produit</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 focus:border-secondary outline-none"
                placeholder="Ex: PC Portable Dell Latitude 5520"
                data-testid="product-name-input"
              />
              <NameCleanSuggestion
                currentName={form.name}
                onApply={(newName) => handleChange('name', newName)}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Catégorie</label>
                <select
                  value={form.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-category-select"
                >
                  {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                    <option key={slug} value={slug}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">État</label>
                <select
                  value={form.condition}
                  onChange={e => handleChange('condition', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-condition-select"
                >
                  <option value="reconditionne">Reconditionné</option>
                  <option value="neuf">Neuf</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Grade (optionnel)</label>
                <select
                  value={form.grade}
                  onChange={e => handleChange('grade', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-grade-select"
                >
                  <option value="">Aucun</option>
                  <option value="A">Grade A</option>
                  <option value="B">Grade B</option>
                  <option value="C">Grade C</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Prix TTC (€)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={form.price}
                  onChange={e => handleChange('price', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  placeholder="299.00"
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">État commercial</label>
                <select
                  value={form.status}
                  onChange={e => handleChange('status', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-status-select"
                >
                  <option value="available">Disponible</option>
                  <option value="reserved">Réservé</option>
                  <option value="sold">Vendu</option>
                </select>
              </div>
            </div>

            {/* Toggle visibilité — axe orthogonal au statut commercial.
                Permet de garder un produit "Vendu" publié pour le SEO et la
                preuve sociale, ou de masquer temporairement un "Disponible". */}
            <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
              <div>
                <label className="text-sm font-medium text-primary">Publié sur la boutique</label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {form.archived
                    ? 'Fiche masquée du catalogue public.'
                    : 'Fiche visible des visiteurs (même si Vendu ou Réservé).'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!form.archived}
                onClick={() => handleChange('archived', !form.archived)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary/40 ${
                  !form.archived ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
                data-testid="product-published-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform translate-y-0.5 ${
                    !form.archived ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1.5">Description courte</label>
              <textarea
                value={form.description}
                onChange={e => handleChange('description', e.target.value)}
                rows={3}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none resize-none"
                placeholder="Description en 2-3 lignes..."
                data-testid="product-description-input"
              />
            </div>
            <AIDescriptionPanel
              productData={form}
              currentDescription={form.description}
              onDescriptionAccepted={(desc) => handleChange('description', desc)}
            />
          </div>

          {/* Specs */}
          <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
            <h3 className="font-heading font-semibold text-primary">Caractéristiques (optionnel)</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Processeur</label>
                <input
                  type="text"
                  value={form.processor}
                  onChange={e => handleChange('processor', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none"
                  placeholder="Intel Core i5-1145G7"
                  data-testid="product-processor-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">RAM</label>
                <input
                  type="text"
                  value={form.ram}
                  onChange={e => handleChange('ram', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none"
                  placeholder="16 Go DDR4"
                  data-testid="product-ram-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Stockage</label>
                <input
                  type="text"
                  value={form.storage}
                  onChange={e => handleChange('storage', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none"
                  placeholder="SSD 256 Go"
                  data-testid="product-storage-input"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Taille écran</label>
                <input
                  type="text"
                  value={form.screen_size}
                  onChange={e => handleChange('screen_size', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none"
                  placeholder="15.6 pouces Full HD"
                  data-testid="product-screen-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Garantie</label>
                <select
                  value={form.warranty}
                  onChange={e => handleChange('warranty', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-warranty-select"
                >
                  <option value="3 mois">3 mois</option>
                  <option value="6 mois">6 mois</option>
                  <option value="12 mois">12 mois</option>
                  <option value="24 mois constructeur">24 mois constructeur</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Stock</label>
                <input
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={e => handleChange('stock', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  data-testid="product-stock-input"
                />
              </div>
            </div>
          </div>

          <RepairabilityPanel
            scores={repairability}
            onScoresChange={setRepairability}
            status={repairabilityStatus}
            onStatusChange={setRepairabilityStatus}
            productName={form.name}
          />

          {/* Images */}
          {isEdit && (
            <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
              <h3 className="font-heading font-semibold text-primary">Photos ({images.length}/5)</h3>

              <div className="flex flex-wrap gap-3">
                {images.map((img, i) => (
                  <div key={img} className="relative group w-24 h-24 rounded-sm overflow-hidden border border-slate-200">
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleImageDelete(img)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}

                {images.length < 5 && (
                  <label className="w-24 h-24 rounded-sm border-2 border-dashed border-slate-200 hover:border-secondary/50 flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="image-upload-btn">
                    {uploading ? (
                      <Loader2 className="w-5 h-5 text-secondary animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-slate-500 mb-1" />
                        <span className="text-[10px] text-slate-500">Ajouter</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] text-slate-500">JPG, PNG ou WebP. Max 5 images. Redimensionnement auto.</p>
            </div>
          )}

          {!isEdit && (
            <div className="bg-white rounded-sm border border-slate-200 p-6">
              <p className="text-sm text-slate-600">Les photos pourront être ajoutées après la création du produit.</p>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-8 py-3 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="product-submit-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Enregistrer' : 'Créer le produit'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/my/produits')}
              className="bg-slate-100 text-primary font-medium rounded-full px-6 py-3 text-sm hover:bg-white/20 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>

        {/* Colonne aperçu (xl+ uniquement) — sticky pour suivre le scroll
            du formulaire. Rend une mini fiche produit fidèle au rendu boutique
            pour valider la mise en page de la description Markdown, etc. */}
        <aside className="hidden xl:block">
          <div className="sticky top-6">
            <h3 className="font-heading text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Aperçu fiche produit
            </h3>
            <ProductPreviewPanel form={form} />
          </div>
        </aside>
        </div>
      </div>
    </AdminLayout>
  );
}

/**
 * Suggère un nom commercial propre en s'appuyant sur des règles statiques
 * côté backend (mapping ASUSTeK COMPUTER INC. → ASUS, etc.). Affiche un
 * bandeau si la suggestion diffère du nom actuel. Le tech valide ou ignore —
 * jamais d'écrasement automatique (boss feedback 2026-05-28).
 */
function NameCleanSuggestion({ currentName, onApply }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Debounce léger : ne tape pas l'API à chaque keystroke. On re-suggère 800ms
  // après le dernier changement de nom (et si la suggestion précédente a été
  // dismissed, on attend un nouveau changement de nom pour réessayer).
  useEffect(() => {
    setDismissed(false);  // un nouveau texte = nouvelle chance de suggérer
  }, [currentName]);

  useEffect(() => {
    if (!currentName || currentName.trim().length < 3) {
      setSuggestion(null);
      return;
    }
    if (dismissed) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await suggestCleanName(currentName);
        if (res?.data?.changed) {
          setSuggestion(res.data);
        } else {
          setSuggestion(null);
        }
      } catch {
        setSuggestion(null);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [currentName, dismissed]);

  if (!suggestion || dismissed) return null;

  return (
    <div className="mt-2 p-3 rounded-sm border border-violet-300 bg-violet-50 flex items-start gap-3">
      <Sparkles className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-violet-800 mb-0.5">
          Nom commercial suggéré
        </div>
        <div className="text-sm text-primary font-medium break-words">
          {suggestion.suggested_name}
        </div>
        {suggestion.reasons?.length > 0 && (
          <div className="text-[10px] text-violet-700/80 mt-1">
            {suggestion.reasons.join(' · ')}
          </div>
        )}
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          type="button"
          onClick={() => { onApply(suggestion.suggested_name); setDismissed(true); }}
          className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-violet-600 text-white hover:bg-violet-700 transition-colors"
        >
          Appliquer
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-xs font-medium uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Ignorer
        </button>
      </div>
    </div>
  );
}

/**
 * Mini preview de la fiche produit telle que le client la verra.
 * Rendu Markdown pour la description (cohérent avec ProductDetail.js).
 */
function ProductPreviewPanel({ form }) {
  const hasContent = form.name || form.description || form.processor || form.ram;
  if (!hasContent) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-8 text-center text-sm text-slate-500">
        Commence à remplir le formulaire pour voir l'aperçu en direct.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
      {/* Header produit */}
      <div className="p-5 border-b border-slate-200 bg-slate-50">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
          {form.category || 'pc'} {form.condition === 'reconditionne' ? '· reconditionné' : ''}
        </div>
        <h2 className="font-heading font-bold text-xl text-primary leading-tight">
          {form.name || 'Nom du produit'}
        </h2>
        {form.grade && (
          <span className="inline-block mt-2 text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-secondary/10 text-secondary border border-secondary/30 rounded-sm">
            Grade {form.grade}
          </span>
        )}
      </div>

      {/* Prix + statut */}
      <div className="p-5 border-b border-slate-200 flex items-baseline gap-3">
        <span className="font-heading font-bold text-3xl text-secondary">
          {form.price ? `${Number(form.price).toFixed(0)} €` : '— €'}
        </span>
        <span className="text-xs text-slate-500">TTC</span>
        <span className={`ml-auto text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
          form.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
          form.status === 'reserved' ? 'bg-slate-200 text-slate-600' :
          'bg-slate-100 text-slate-500'
        }`}>
          {form.status === 'available' ? 'Disponible' : form.status === 'reserved' ? 'Réservé' : 'Vendu'}
        </span>
      </div>

      {/* Specs rapides */}
      {(form.processor || form.ram || form.storage || form.screen_size) && (
        <div className="p-5 border-b border-slate-200 space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Caractéristiques</div>
          {form.processor && <div className="flex justify-between text-sm"><span className="text-slate-500">Processeur</span><span className="text-primary font-medium">{form.processor}</span></div>}
          {form.ram && <div className="flex justify-between text-sm"><span className="text-slate-500">RAM</span><span className="text-primary font-medium">{form.ram}</span></div>}
          {form.storage && <div className="flex justify-between text-sm"><span className="text-slate-500">Stockage</span><span className="text-primary font-medium">{form.storage}</span></div>}
          {form.screen_size && <div className="flex justify-between text-sm"><span className="text-slate-500">Écran</span><span className="text-primary font-medium">{form.screen_size}</span></div>}
          {form.warranty && <div className="flex justify-between text-sm"><span className="text-slate-500">Garantie</span><span className="text-primary font-medium">{form.warranty}</span></div>}
        </div>
      )}

      {/* Description courte (paragraphes) */}
      {form.description && (
        <div className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Description</div>
          <div className="text-sm text-slate-700 leading-relaxed space-y-2">
            {form.description.split(/\n{2,}/).filter(Boolean).map((para, i) => (
              <p key={i} className="whitespace-pre-line">{para.trim()}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
